import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {offerApi, RequestStatus} from "../../../api/apiConfiguration";
import {RootState} from "../../../app/store";
import {SelectOption} from "../../elements/select/SelectDropdown";
import {
    OfferInListDTO,
    OfferOptionsRequest,
    OfferTableRequest,
    OfferTableResponse,
    UpdateOfferStatusRequest
} from "../../../api/generated";

export const OFFERS_TABLE_ITEMS_ON_PAGE = 10

export type OfferTableRequestPartial = Partial<OfferTableRequest>

export interface OfferState {
    makeOptions: SelectOption<string>[]
    modelOptions: SelectOption<string>[]
    sourceNameOptions: SelectOption<string>[]
    totalCount: number
    offerList: OfferInListDTO[]
    offerListRequestStatus: RequestStatus
    /** Covers the make, model and source-name dropdowns, which are always loaded together. */
    offerOptionsRequestStatus: RequestStatus
    offerTableRequest: OfferTableRequest
}

export const defaultOfferTableRequestRequest = {
    pageNumber: 0,
    pageSize: OFFERS_TABLE_ITEMS_ON_PAGE,
} as OfferTableRequest

const buildInitialState = (): OfferState => {
    return {
        makeOptions: [] as SelectOption<string>[],
        modelOptions: [] as SelectOption<string>[],
        sourceNameOptions: [] as SelectOption<string>[],
        totalCount: 0,
        offerList: [],
        offerListRequestStatus: RequestStatus.NONE,
        offerOptionsRequestStatus: RequestStatus.NONE,
        offerTableRequest: defaultOfferTableRequestRequest
    }
}

const initialState: OfferState = buildInitialState()

export const fetchOfferList = createAsyncThunk<OfferTableResponse, OfferTableRequest, {
    state: RootState
}>(
    "offerList/fetchOfferList",
    (payload) => {
        return offerApi.getOfferTable({
            offerTableRequest: payload
        });
    }
);

export const fetchOfferMakeOptions = createAsyncThunk<string[], OfferOptionsRequest, { state: RootState }>(
    "offerList/fetchOfferMakeOptions",
    (payload) => {
        return offerApi.getMakeOptions({offerOptionsRequest: payload})
    }
);

export const fetchOfferModelOptions = createAsyncThunk<string[], OfferOptionsRequest, { state: RootState }>(
    "offerList/fetchOfferModelOptions",
    (payload) => {
        return offerApi.getModelOptions({offerOptionsRequest: payload})
    }
);

export const fetchOfferSourceNameOptions = createAsyncThunk<string[], OfferOptionsRequest, { state: RootState }>(
    "offerList/fetchOfferSourceNameOptions",
    (payload) => {
        return offerApi.getSourceNameOptions({offerOptionsRequest: payload})
    }
);

export const toggleOfferStatus = createAsyncThunk<OfferInListDTO, UpdateOfferStatusRequest, { state: RootState }>(
    "offerList/toggleOfferStatus",
    (payload) => {
        return offerApi.updateOfferStatus(payload)
    }
);

export const offerListSlice = createSlice({
    name: 'offerList',
    initialState,
    reducers: {
        clearOfferListSlice: () => {
            return buildInitialState();
        },
        updateOfferTableRequest: (state, {payload}: PayloadAction<OfferTableRequestPartial>) => {
            if (!payload.pageNumber) {
                payload.pageNumber = 0
            }
            state.offerTableRequest = {
                ...state.offerTableRequest,
                ...payload
            }
        },
        resetOfferTableRequest: (state) => {
            const {sortBy, sortDirection} = state.offerTableRequest
            state.offerTableRequest = sortBy
                ? {...defaultOfferTableRequestRequest, sortBy, sortDirection}
                : defaultOfferTableRequestRequest
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchOfferList.pending, (state) => {
            state.offerListRequestStatus = RequestStatus.PENDING;
        });
        builder.addCase(fetchOfferList.fulfilled, (state, {payload}) => {
            state.offerList = payload.offers;
            state.totalCount = payload.totalCount;
            state.offerListRequestStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(fetchOfferList.rejected, (state) => {
            state.offerListRequestStatus = RequestStatus.REJECTED;
        });
        builder.addCase(fetchOfferMakeOptions.rejected, (state) => {
            state.offerOptionsRequestStatus = RequestStatus.REJECTED;
        });
        builder.addCase(fetchOfferModelOptions.rejected, (state) => {
            state.offerOptionsRequestStatus = RequestStatus.REJECTED;
        });
        builder.addCase(fetchOfferSourceNameOptions.rejected, (state) => {
            state.offerOptionsRequestStatus = RequestStatus.REJECTED;
        });
        builder.addCase(fetchOfferMakeOptions.fulfilled, (state, {payload}) => {
            state.makeOptions = payload.map(option => {
                if (option === "*") {
                    return {
                        value: option,
                        label: "All Makes"
                    } as SelectOption<string>
                } else {
                    return {
                        value: option,
                        label: option
                    } as SelectOption<string>
                }
            })
        });
        builder.addCase(fetchOfferModelOptions.fulfilled, (state, {payload}) => {
            state.modelOptions = payload.map(option => {
                if (option === "*") {
                    return {
                        value: option,
                        label: "All Models"
                    } as SelectOption<string>
                } else {
                    return {
                        value: option,
                        label: option
                    } as SelectOption<string>
                }
            })
        });
        builder.addCase(fetchOfferSourceNameOptions.fulfilled, (state, {payload}) => {
            state.sourceNameOptions = payload.map(option => {
                return {
                    value: option,
                    label: option
                } as SelectOption<string>
            })
        });
        builder.addCase(toggleOfferStatus.fulfilled, (state, {payload}) => {
            const index = state.offerList.findIndex(offer => offer.publicId === payload.publicId)
            state.offerList[index] = payload
        });
    },
});

export const {
    clearOfferListSlice,
    updateOfferTableRequest,
    resetOfferTableRequest
} = offerListSlice.actions;

export const offerListSelector = (state: RootState) => state.offerList.offerList;
export const offerListTotalCountSelector = (state: RootState): number => state.offerList.totalCount;
export const offerTableRequestSelector = (state: RootState): OfferTableRequest => state.offerList.offerTableRequest;
export const offerListRequestStatusSelector = (state: RootState): RequestStatus => state.offerList.offerListRequestStatus;
export const offerOptionsRequestStatusSelector = (state: RootState): RequestStatus => state.offerList.offerOptionsRequestStatus;
export const offerMakeOptionsSelector = (state: RootState): SelectOption<string>[] => state.offerList.makeOptions;
export const offerModelOptionsSelector = (state: RootState): SelectOption<string>[] => state.offerList.modelOptions;
export const offerSourceNameOptionsSelector = (state: RootState): SelectOption<string>[] => state.offerList.sourceNameOptions;
